/**
 * CompanyCardContent - Reusable company display content.
 *
 * Shows company avatar, name, and metadata (industry, headcount, location).
 * Used inside ObjectCard for bordered display, or standalone for inline use.
 *
 * Self-hydrating: pass `entityId` + `orgId` and the card resolves its own
 * fields from the centralized CRM store via `useCrmObjectSnapshot`. Each
 * field renders a skeleton in its own slot until its value arrives, so
 * nothing pops in from a "–" placeholder. Explicit prop overrides win
 * over store-resolved values when provided.
 */

import { Building01, MarkerPin01, Users01 } from '@untitledui/icons';
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
} from 'react';

import { Avatar } from '@/lib/base/avatar/avatar';
import { Skeleton } from '@/lib/base/skeleton/skeleton';
import { useCrmObjectSnapshot } from '@/lib/crm/hooks';
import {
  getFailureVersion,
  hasFailedFetch,
  subscribeToFetchFailures,
} from '@/lib/crm/object-cards/entityFetchBatcher';
import { createFaviconLoadGuard } from '@/lib/crm/utils/avatar';
import type { CrmObject } from '@/types/crm';
import { getPropertyValue } from '@/types/crm';
import { cx } from '@/utils/cx';

import { CompactCardLayout } from './CompactCardLayout';
import type { MetadataItem } from './MetadataRow';
import { MetadataRow } from './MetadataRow';

export interface CompanyCardContentProps {
  /**
   * Store-fetch mode: when `entityId` is provided, the card subscribes to
   * the CRM store and fills in any undefined props from the live object.
   * Each missing field renders a skeleton until the data arrives.
   */
  entityId?: string;
  orgId?: number;

  /** Explicit overrides — win over store-resolved values when provided. */
  name?: string;
  /** Avatar image URL (favicon, logo) */
  avatarUrl?: string;
  /** Initials fallback for avatar */
  initials?: string;
  /** Industry value */
  industry?: string;
  /** Headcount or employee range */
  headcount?: string;
  /** Location string */
  location?: string;
  /** When provided, clicking the name triggers this callback (e.g. open CRM slideout) */
  onClick?: () => void;
  /** Compact mode — avatar + name + type pill only */
  compact?: boolean;
  /** Compact mode only — omit the type pill (see CompactCardLayout). */
  hideTypeBadge?: boolean;
  /**
   * Legacy prop. When true, forces the metadata row into its skeleton
   * state regardless of store hydration. Preferred path is to pass
   * `entityId` and let the card derive loading state from the store
   * subscription.
   */
  isLoading?: boolean;
  className?: string;
}

/** Pull a string value out of a CrmObject's properties map. */
function readStringProp(
  object: CrmObject | undefined,
  key: string
): string | undefined {
  if (!object?.properties) {
    return undefined;
  }
  const raw = getPropertyValue<string | string[] | number>(
    object.properties[key]
  );
  if (typeof raw === 'number') {
    return String(raw);
  }
  if (typeof raw === 'string') {
    return raw.trim() || undefined;
  }
  if (Array.isArray(raw) && typeof raw[0] === 'string') {
    return raw[0].trim() || undefined;
  }
  return undefined;
}

export function CompanyCardContent(props: CompanyCardContentProps) {
  const {
    entityId,
    orgId,
    name: nameProp,
    avatarUrl: avatarUrlProp,
    initials: initialsProp,
    industry: industryProp,
    headcount: headcountProp,
    location: locationProp,
    onClick,
    compact,
    hideTypeBadge,
    isLoading: isLoadingProp = false,
    className,
  } = props;
  const [isFaviconFailed, setIsFaviconFailed] = useState(false);

  // Subscribe to the company's store entry. Hook always runs; when entityId
  // is undefined it short-circuits to no-op inside useCrmObjectSnapshot.
  const storeObject = useCrmObjectSnapshot(entityId, orgId);

  // Subscribe to fetch failure notifications so a failed fetch exits the
  // skeleton state instead of staying in "hydrating" forever.
  useSyncExternalStore(
    subscribeToFetchFailures,
    getFailureVersion,
    getFailureVersion
  );
  const isSourceHydrating =
    entityId != null && !storeObject && !hasFailedFetch(entityId);

  // Per-field resolution: explicit prop wins; else derive from store; else
  // undefined. `isLoading` for a field is "prop missing AND the store
  // fetch is still in flight".
  //
  // Name resolution walks the standard display-name fallbacks so existing
  // companies don't render as "New company" after hydration. `ahoy_company`
  // stores its canonical name in `ahoy_name` (see entityTypeRegistry's
  // detailFieldOrder); older data may also use `ahoy_display_name` or
  // `ahoy_company_name`, so we check each in turn.
  const name =
    nameProp ??
    readStringProp(storeObject, 'ahoy_display_name') ??
    readStringProp(storeObject, 'ahoy_name') ??
    readStringProp(storeObject, 'ahoy_company_name') ??
    undefined;
  const isNameLoading = nameProp == null && isSourceHydrating;

  const avatarUrl =
    avatarUrlProp ?? readStringProp(storeObject, 'ahoy_avatar_url');
  const initials = initialsProp;

  const industry = industryProp ?? readStringProp(storeObject, 'ahoy_industry');
  const isIndustryLoading = industryProp == null && isSourceHydrating;

  const headcount =
    headcountProp ??
    readStringProp(storeObject, 'ahoy_headcount_range') ??
    readStringProp(storeObject, 'ahoy_employee_count');
  const isHeadcountLoading = headcountProp == null && isSourceHydrating;

  const city = readStringProp(storeObject, 'ahoy_city');
  const country = readStringProp(storeObject, 'ahoy_country');
  const derivedLocation = [city, country].filter(Boolean).join(', ');
  const location =
    locationProp ??
    readStringProp(storeObject, 'ahoy_location') ??
    derivedLocation ??
    undefined;
  const isLocationLoading = locationProp == null && isSourceHydrating;

  useEffect(() => {
    setIsFaviconFailed(false);
  }, [avatarUrl]);

  const handleFaviconLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      createFaviconLoadGuard(() => setIsFaviconFailed(true))(e);
    },
    [] // setIsFaviconFailed is a stable state setter
  );

  const handleNameClick = useCallback(
    (e: React.MouseEvent) => {
      if (onClick) {
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const handleNameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (onClick && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }
    },
    [onClick]
  );

  const metadataItems = useMemo<MetadataItem[]>(
    () => [
      {
        key: 'industry',
        icon: Building01,
        fieldName: 'Industry',
        label: industry || '',
        isLoading: isIndustryLoading,
      },
      {
        key: 'headcount',
        icon: Users01,
        fieldName: 'Headcount',
        label: headcount || '',
        isLoading: isHeadcountLoading,
      },
      {
        key: 'location',
        icon: MarkerPin01,
        fieldName: 'Location',
        label: location || '',
        isLoading: isLocationLoading,
      },
    ],
    [
      industry,
      headcount,
      location,
      isIndustryLoading,
      isHeadcountLoading,
      isLocationLoading,
    ]
  );

  // Auto-collapse to compact when the metadata row would otherwise render
  // only "–" placeholders. Full mode with three empty slots reads as
  // broken; the compact layout keeps just avatar + name + type pill.
  // Only kicks in after hydration settles; explicit `compact` still wins,
  // and the legacy `isLoading` prop keeps the full skeleton row.
  const hasSecondaryContent =
    !!industry ||
    !!headcount ||
    !!location ||
    isIndustryLoading ||
    isHeadcountLoading ||
    isLocationLoading;
  const shouldRenderCompact =
    compact || (!isLoadingProp && !isSourceHydrating && !hasSecondaryContent);

  if (shouldRenderCompact) {
    return (
      <CompactCardLayout
        name={name ?? ''}
        entityTypeName='ahoy_company'
        // Same favicon-quality guard as full mode: a tiny default globe
        // falls back to initials instead of rendering blurry.
        avatarUrl={!isFaviconFailed ? avatarUrl : undefined}
        initials={initials}
        onAvatarLoad={handleFaviconLoad}
        isLoading={isNameLoading}
        hideTypeBadge={hideTypeBadge}
        onClick={onClick}
        className={className}
      />
    );
  }

  // Full mode renders metadata either fully as skeletons (legacy
  // `isLoading` prop) or per-field via the `isLoading` flag on each
  // MetadataItem. Either way the sub-card footprint stays stable.
  return (
    <div className={cx('space-y-1.5', className)}>
      {/* Top row: avatar + name (left-aligned) */}
      <div className='flex items-center gap-2'>
        <Avatar
          size='xs'
          src={!isFaviconFailed ? avatarUrl : undefined}
          initials={initials || (name ? name.charAt(0).toUpperCase() : '')}
          onLoad={avatarUrl ? handleFaviconLoad : undefined}
        />
        {isNameLoading ? (
          <Skeleton variant='text' width={140} height={14} />
        ) : (
          <span
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick ? handleNameClick : undefined}
            onKeyDown={onClick ? handleNameKeyDown : undefined}
            className={cx(
              'text-sm font-medium text-fg-primary truncate',
              onClick &&
                'cursor-pointer hover:underline hover:text-fg-brand-primary'
            )}
          >
            {name ?? 'New company'}
          </span>
        )}
      </div>

      {/* Bottom row: industry, headcount, location. `isLoadingProp` is the
          legacy path where the caller knows the whole metadata block is
          pending; otherwise each item handles its own loading slot. */}
      {isLoadingProp ? (
        <div className='pl-8 flex items-center gap-4'>
          <Skeleton variant='text' width={80} height={12} />
          <Skeleton variant='text' width={56} height={12} />
          <Skeleton variant='text' width={96} height={12} />
        </div>
      ) : (
        <MetadataRow items={metadataItems} className='pl-8' />
      )}
    </div>
  );
}
